import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  SegmentLocation,
  SegmentStatus,
  TAutoSavingSegmentData,
  TExtendedSegment,
  TSegmentLocation,
  TServerSegment,
} from '@/shared/types/Editor';

import { getIntersectingTimestamps } from './utils/getIntersectingTimestamps';

type TNewSegmentData = {
  location: TSegmentLocation;
  parentSegmentId: TServerSegment['id'];
  segment: TServerSegment;
};

type TSegmentDataToSave = {
  [segmentId: TExtendedSegment['id']]: TAutoSavingSegmentData;
};

type TEditorStore = {
  // Actual data from server
  segmentsFromServerMap: {
    [segmentId: TServerSegment['id']]: TServerSegment;
  };
  // Changeable data
  segmentsMap: {
    [segmentId: TExtendedSegment['id']]: TExtendedSegment;
  };
  focusedSegmentId: null | TServerSegment['id'];
  isAutoSaving: boolean;
  isVoiceSuggesting: boolean;
  newSegmentsData: TNewSegmentData[];
  // The storage for auto-saving data
  segmentsDataToSave: TSegmentDataToSave;
  segmentsIds: TExtendedSegment['id'][];
};

const initialState: TEditorStore = {
  focusedSegmentId: null,
  isAutoSaving: false,
  isVoiceSuggesting: false,
  newSegmentsData: [],
  segmentsDataToSave: {},
  segmentsFromServerMap: {},
  segmentsIds: [],
  segmentsMap: {},
};

const normalizeSegment = (segment: TServerSegment): TServerSegment => {
  return {
    ...segment,
    dst: segment.dst || { lang: '', text: '' },
    src: segment.src || { lang: '', text: '' },
  };
};

const editorSlice = createSlice({
  name: 'editor',
  initialState,
  reducers: {
    clearVoiceCloneErrorForSpeaker: (state, { payload }: PayloadAction<string>) => {
      Object.values(state.segmentsMap).forEach((segment) => {
        if (segment.speaker === payload && segment.isVoiceCloneError) {
          segment.isVoiceCloneError = false;
        }
      });
    },

    considerNewSegmentCreated: (
      state,
      {
        payload,
      }: PayloadAction<{
        temporarySegmentId: TServerSegment['id'];
      }>,
    ) => {
      state.newSegmentsData = state.newSegmentsData.filter(
        (segment) => segment.segment.id !== payload.temporarySegmentId,
      );
    },

    insertSegment: (state, { payload }: PayloadAction<TNewSegmentData>) => {
      const parentSegmentIdx = state.segmentsIds.indexOf(payload.parentSegmentId);

      if (parentSegmentIdx !== -1) {
        const newSegmentIndex = payload.location === SegmentLocation.Top ? parentSegmentIdx : parentSegmentIdx + 1;

        state.segmentsMap[payload.segment.id] = payload.segment;
        state.segmentsIds.splice(newSegmentIndex, 0, payload.segment.id);
      }
    },

    // Overlapped timestamps logic
    recalculateOverlappedTimestamps: (state) => {
      state.segmentsIds.forEach((id) => {
        delete state.segmentsMap[id].overlappedTimestamps;
      });

      // segmentsIdsToCheck is for optimizing loop iterations
      const segmentsIdsToCheck = [...state.segmentsIds];

      state.segmentsIds.forEach((id) => {
        const overlappedTimestamps = getIntersectingTimestamps({
          segment: state.segmentsMap[id],
          segmentsIdsToCheck,
          segmentsMap: state.segmentsMap,
        });

        segmentsIdsToCheck.shift();

        Object.keys(overlappedTimestamps).forEach((segmentId) => {
          const segment = state.segmentsMap[segmentId];
          const isAlreadyExist = Boolean(segment?.overlappedTimestamps?.length);

          if (isAlreadyExist) {
            state.segmentsMap[segmentId].overlappedTimestamps = Array.from(
              new Set([
                ...(state.segmentsMap[segmentId].overlappedTimestamps || []),
                ...overlappedTimestamps[segmentId],
              ]),
            );
            return;
          }

          if (state.segmentsMap[segmentId]) {
            state.segmentsMap[segmentId].overlappedTimestamps = overlappedTimestamps[segmentId];
          }
        });
      });
    },

    updSegment: (state, { payload }: PayloadAction<{ id: TExtendedSegment['id'] } & Partial<TExtendedSegment>>) => {
      if (state.segmentsMap[payload.id]) {
        state.segmentsMap[payload.id] = { ...state.segmentsMap[payload.id], ...payload };
      }
    },

    updSegmentAfterProcessing: (state, { payload }: PayloadAction<TServerSegment[]>) => {
      payload.forEach((updatedSegment) => {
        const segmentFromServer = state.segmentsFromServerMap[updatedSegment.id];
        const isFinalStatus = updatedSegment.status !== SegmentStatus.Processing;

        if (segmentFromServer && isFinalStatus) {
          state.segmentsFromServerMap[updatedSegment.id] = updatedSegment;

          state.segmentsMap[updatedSegment.id].status = updatedSegment.status;
          state.segmentsMap[updatedSegment.id].src = updatedSegment.src;
          state.segmentsMap[updatedSegment.id].dst = updatedSegment.dst;

          if (updatedSegment.status === SegmentStatus.Error) {
            state.segmentsMap[updatedSegment.id].isSavingError = true;
          }
        }
      });
    },

    updSegmentFromServer: (state, { payload }: PayloadAction<TServerSegment>) => {
      state.segmentsFromServerMap[payload.id] = payload;
    },

    updSegmentIsSavingError: (
      state,
      { payload }: PayloadAction<{ id: TExtendedSegment['id'] } & Partial<TExtendedSegment>>,
    ) => {
      const segment = state.segmentsMap[payload.id];

      if (segment.isSavingError !== payload.isSavingError) {
        segment.isSavingError = payload.isSavingError;
      }
    },

    // New segments logic
    addNewSegment: (
      state,
      {
        payload,
      }: PayloadAction<{ location: TSegmentLocation; parentSegment: TServerSegment; segment: TServerSegment }>,
    ) => {
      state.newSegmentsData.push({
        location: payload.location,
        parentSegmentId: payload.parentSegment.id,
        segment: payload.segment,
      });
    },

    removeSegment: (
      state,
      { payload }: PayloadAction<{ id: TServerSegment['id']; leaveInSegmentsFromServer?: boolean }>,
    ) => {
      state.segmentsIds = state.segmentsIds.filter((id) => id !== payload.id);

      delete state.segmentsMap[payload.id];
      delete state.segmentsDataToSave[payload.id];

      if (!payload.leaveInSegmentsFromServer) {
        delete state.segmentsFromServerMap[payload.id];
      }
    },

    removeSegmentDataToSave: (
      state,
      { payload }: PayloadAction<{ changedDataKey?: keyof TAutoSavingSegmentData; segmentId: TServerSegment['id'] }>,
    ) => {
      if (!state.segmentsDataToSave[payload.segmentId]) {
        return;
      }

      // We can remove a particular field in the segmentsDataToSave object:
      if (payload.changedDataKey) {
        delete state.segmentsDataToSave[payload.segmentId][payload.changedDataKey];

        const isEmptyObject = Object.keys(state.segmentsDataToSave[payload.segmentId]).length === 0;

        if (isEmptyObject) {
          delete state.segmentsDataToSave[payload.segmentId];
        }
      } else {
        // Or remove the whole segmentsDataToSave object
        delete state.segmentsDataToSave[payload.segmentId];
      }
    },

    // State resetting
    resetEditorState: () => initialState,

    setAllSegments: (state, { payload }: PayloadAction<TServerSegment[]>) => {
      const segmentsMap: Record<string, TServerSegment> = {};

      for (const segment of payload) {
        const normalizedSegment = normalizeSegment(segment);
        segmentsMap[normalizedSegment.id] = normalizedSegment;
      }

      state.segmentsFromServerMap = segmentsMap;
      state.segmentsMap = segmentsMap;
      state.segmentsIds = payload.map(({ id }) => id);
    },

    // Focused segment logic
    setFocusedSegmentId: (state, { payload }: PayloadAction<TEditorStore['focusedSegmentId']>) => {
      state.focusedSegmentId = payload;
    },

    setIsAutoSaving: (state, { payload }: PayloadAction<boolean>) => {
      state.isAutoSaving = payload;
    },

    // Voice suggest logic
    setIsVoiceSuggesting: (state, { payload }: PayloadAction<boolean>) => {
      state.isVoiceSuggesting = payload;
    },

    // Auto save logic
    setSegmentDataToSave: (
      state,
      { payload }: PayloadAction<{ id: TServerSegment['id'] } & TAutoSavingSegmentData>,
    ) => {
      state.segmentsDataToSave[payload.id] = state.segmentsDataToSave[payload.id]
        ? { ...state.segmentsDataToSave[payload.id], ...payload }
        : payload;
    },

    setServerSegments: (state, { payload }: PayloadAction<TServerSegment[]>) => {
      const segmentsMap: Record<string, TServerSegment> = {};

      for (const segment of payload) {
        const normalizedSegment = normalizeSegment(segment);
        segmentsMap[normalizedSegment.id] = normalizedSegment;
      }

      state.segmentsFromServerMap = segmentsMap;
    },

    setStatus: (state, { payload }: PayloadAction<{ id: TServerSegment['id']; status: TServerSegment['status'] }>) => {
      state.segmentsMap[payload.id].status = payload.status;
    },
  },
});

export const editorReducer = editorSlice.reducer;

export const {
  clearVoiceCloneErrorForSpeaker,
  considerNewSegmentCreated,
  insertSegment,
  recalculateOverlappedTimestamps,
  updSegment,
  updSegmentAfterProcessing,
  updSegmentFromServer,
  updSegmentIsSavingError,
  addNewSegment,
  removeSegment,
  removeSegmentDataToSave,
  resetEditorState,
  setAllSegments,
  setFocusedSegmentId,
  setIsAutoSaving,
  setIsVoiceSuggesting,
  setSegmentDataToSave,
  setServerSegments,
  setStatus,
} = editorSlice.actions;
